import { useCallback, useId, useMemo, useState } from 'react';

import { CounterpartyConnectionsFilterPanel } from './CounterpartyConnectionsFilterPanel';
import {
  type ConnectionsPanelFilter,
  type ConnectionsRiskFilterCategory,
  type ConnectionsSegmentMode,
  applyCounterpartyConnectionsFilters,
  filterNamedConnections,
  type CounterpartyConnectionItem,
} from './CounterpartyConnections.helpers';

export function useCounterpartyConnectionsView(
  connections: CounterpartyConnectionItem[] | null | undefined,
) {
  const headingId = useId();
  const [segment, setSegment] = useState<ConnectionsSegmentMode>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterRisk, setFilterRisk] =
    useState<ConnectionsRiskFilterCategory>('all');

  const panelFilter = useMemo<ConnectionsPanelFilter>(
    () => ({
      search: filterSearch,
      riskCategory: filterRisk,
    }),
    [filterRisk, filterSearch],
  );

  const { namedConnections, visibleConnections } = useMemo(() => {
    const named = filterNamedConnections(connections);
    return {
      namedConnections: named,
      visibleConnections: applyCounterpartyConnectionsFilters(
        named,
        segment,
        panelFilter,
      ),
    };
  }, [connections, panelFilter, segment]);

  const filterPanelActive =
    filterSearch.trim().length > 0 || filterRisk !== 'all';

  const resetPanelFilters = useCallback(() => {
    setFilterSearch('');
    setFilterRisk('all');
  }, []);

  const filterPanelElement = useMemo(
    () => (
      <CounterpartyConnectionsFilterPanel
        search={filterSearch}
        onSearchChange={setFilterSearch}
        riskCategory={filterRisk}
        onRiskCategoryChange={setFilterRisk}
        filterActive={filterPanelActive}
        onClear={resetPanelFilters}
      />
    ),
    [filterPanelActive, filterRisk, filterSearch, resetPanelFilters],
  );

  return {
    headingId,
    segment,
    setSegment,
    filterOpen,
    setFilterOpen,
    namedConnections,
    visibleConnections,
    filterPanelActive,
    filterPanelElement,
  };
}
